import { Check, CheckCircle, Key, Loader2 } from "lucide-react";
import { useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { extensionService } from "@/lib/services";
import type { ExtensionResponse } from "@/types";

export function CredentialsList({
  extension,
  onUpdated,
}: Readonly<{ extension: ExtensionResponse; onUpdated: () => Promise<void> }>) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState<string | null>(null);
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const handleSave = async (key: string) => {
    if (!editValue.trim()) return;
    try {
      setSaving(true);
      await extensionService.setCredential(extension.uuid, { key, value: editValue });
      setEditingKey(null);
      setEditValue("");
      showToast("Credential saved", "success");
      await onUpdated();
    } catch {
      showToast("Failed to save credential", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (key: string, isEnvOverride: boolean) => {
    const confirmed = await confirm({
      title: isEnvOverride ? "Revert to Environment Variable" : "Clear Credential",
      message: isEnvOverride
        ? `This will remove the stored override and revert to the environment variable. Continue?`
        : `This will remove the stored value for "${key}". Continue?`,
      confirmText: isEnvOverride ? "Revert" : "Clear",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      setClearing(key);
      await extensionService.clearCredential(extension.uuid, key);
      showToast("Credential cleared", "success");
      await onUpdated();
    } catch {
      showToast("Failed to clear credential", "error");
    } finally {
      setClearing(null);
    }
  };

  if (extension.credentials.length === 0) {
    return <p className="text-content-quaternary text-sm">No credentials configured for this extension.</p>;
  }

  return (
    <div className="space-y-3">
      {extension.credentials.map((cred) => {
        const showClear = cred.isEnvOverride ? cred.hasDbValue : cred.isConfigured;
        const clearLabel = cred.isEnvOverride ? "Revert to env" : "Clear";
        const updateLabel = cred.isEnvOverride ? "Override" : "Update";

        return (
          <div key={cred.key} className="py-3 border-b border-border-secondary last:border-b-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-content-primary font-mono">{cred.key}</p>
                {cred.isEnvOverride && cred.envVarName && !cred.hasDbValue && (
                  <p className="text-xs text-content-quaternary mt-0.5">Using env var: {cred.envVarName}</p>
                )}
                {cred.isEnvOverride && cred.hasDbValue && (
                  <p className="text-xs text-content-quaternary mt-0.5">
                    Overriding env var <span className="font-mono">{cred.envVarName}</span> with stored value
                  </p>
                )}
                {!cred.isSecret && cred.currentValue && (
                  <p className="text-xs text-content-tertiary mt-0.5 font-mono">{cred.currentValue}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {cred.isConfigured ? (
                  <span className="flex items-center gap-1 text-xs text-green-400">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-amber-400">
                    <Key className="w-3.5 h-3.5" />
                    Not set
                  </span>
                )}
                {editingKey !== cred.key && (
                  <div className="flex items-center gap-2">
                    {showClear && (
                      <button
                        type="button"
                        onClick={() => handleClear(cred.key, cred.isEnvOverride)}
                        disabled={clearing === cred.key}
                        className="px-2.5 py-1 text-xs font-medium text-red-400 hover:text-white hover:bg-red-500/20 border border-red-500/30 rounded-md transition disabled:opacity-50"
                      >
                        {clearing === cred.key ? "Clearing..." : clearLabel}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingKey(cred.key);
                        setEditValue("");
                      }}
                      className="px-2.5 py-1 text-xs font-medium text-brand-primary hover:text-white hover:bg-brand-primary/20 border border-brand-primary/30 rounded-md transition"
                    >
                      {updateLabel}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {editingKey === cred.key && (
              <div className="flex items-center gap-2 mt-3">
                <input
                  type={cred.isSecret ? "password" : "text"}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder={cred.isSecret ? "Enter new value..." : cred.currentValue || "Enter value..."}
                  autoFocus
                  className="flex-1 bg-surface-primary border border-border-primary rounded-lg px-3 py-2 text-sm text-content-primary focus:outline-none focus:border-brand-primary"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave(cred.key);
                    if (e.key === "Escape") {
                      setEditingKey(null);
                      setEditValue("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleSave(cred.key)}
                  disabled={saving || !editValue.trim()}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary/90 rounded-lg transition disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingKey(null);
                    setEditValue("");
                  }}
                  className="px-3 py-2 text-sm text-content-tertiary hover:text-content-primary bg-surface-tertiary hover:bg-surface-tertiary/80 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
